import {
  CHECKOUT_BOOLEAN_ATTRIBUTES,
  type CheckoutDropdownGroup,
  type CheckoutElement,
  type CustomCheckoutElementAttributes,
  EDITABLE_OPTION_KEYS,
  isStandardCheckoutElementId,
  type LocaleEntry,
  type LocaleGroup,
  type SparkOptions,
  type StandardCheckoutElement,
} from '../types/spark-options';
import { hasKeys, isPlainObject } from './object';

type LocaleRecord = Record<string, Record<string, string>>;

type SparkJsDropdownGroup = {
  translations?: LocaleRecord;
  items: { value: string; translations: LocaleRecord }[];
};

export type SparkJsCheckoutElement = {
  id?: StandardCheckoutElement;
  name?: string;
  translations?: LocaleRecord;
  attributes?: CustomCheckoutElementAttributes;
  options?: SparkJsDropdownGroup[];
};

/** Blank and repeat locales and keys can't be object keys; validation blocks them before this runs. */
export function toLocaleRecord(texts: LocaleGroup[]): LocaleRecord {
  const out: LocaleRecord = {};

  for (const { locale, entries } of texts) {
    const localeKey = locale.trim();
    if (localeKey === '' || localeKey in out) continue;

    const strings: Record<string, string> = {};
    for (const { key, value } of entries) {
      const entryKey = key.trim();
      if (entryKey !== '' && value !== '' && !(entryKey in strings)) strings[entryKey] = value;
    }
    out[localeKey] = strings;
  }

  return out;
}

export function fromLocaleRecord(value: unknown): LocaleGroup[] {
  if (!isPlainObject(value)) return [];

  return Object.entries(value).map(([locale, stored], id) => {
    const entries: LocaleEntry[] = [];
    if (isPlainObject(stored)) {
      for (const [key, text] of Object.entries(stored)) {
        if (typeof text === 'string') entries.push({ id: entries.length, key, value: text });
      }
    }
    return { id, locale, entries };
  });
}

function toSparkJsDropdownGroup(group: CheckoutDropdownGroup): SparkJsDropdownGroup {
  const out: SparkJsDropdownGroup = {
    items: group.items.map((item) => ({
      value: item.value,
      translations: toLocaleRecord(item.translations),
    })),
  };
  if (group.translations) out.translations = toLocaleRecord(group.translations);
  return out;
}

function toSparkJsCheckoutElement(element: CheckoutElement): SparkJsCheckoutElement {
  if (element.kind === 'standard') {
    const field: SparkJsCheckoutElement = { id: element.id };
    const attributes: CustomCheckoutElementAttributes = {};
    for (const key of CHECKOUT_BOOLEAN_ATTRIBUTES) {
      if (element[key]) attributes[key] = true;
    }
    if (hasKeys(attributes)) field.attributes = attributes;
    return field;
  }

  const field: SparkJsCheckoutElement = { name: element.name };
  const translations = toLocaleRecord(element.translations);
  if (hasKeys(translations)) field.translations = translations;
  if (hasKeys(element.attributes)) field.attributes = element.attributes;
  if (element.options) field.options = element.options.map(toSparkJsDropdownGroup);
  return field;
}

/** Convert the editor's checkout fields into SparkJS's `checkoutCustomElements`. */
export function toSparkJsCheckoutElements(elements: CheckoutElement[]): SparkJsCheckoutElement[] {
  return elements.map(toSparkJsCheckoutElement);
}

export function toSparkJsOptions(options: SparkOptions): Record<string, unknown> {
  const sparkOptions: Record<string, unknown> = {};

  for (const key of EDITABLE_OPTION_KEYS) {
    if (key === 'checkoutCustomElements') {
      sparkOptions.checkoutCustomElements = toSparkJsCheckoutElements(
        options.checkoutCustomElements,
      );
      continue;
    }
    if (key === 'translations') {
      sparkOptions.translations = toLocaleRecord(options.translations);
      continue;
    }
    sparkOptions[key] = options[key];
  }

  return sparkOptions;
}

function fromSparkJsDropdownGroup(group: unknown): Record<string, unknown> {
  if (!isPlainObject(group)) return { items: [] };

  const out: Record<string, unknown> = {
    items: Array.isArray(group.items)
      ? group.items.filter(isPlainObject).map((item) => ({
          value: item.value,
          translations: fromLocaleRecord(item.translations),
        }))
      : [],
  };
  if (group.translations !== undefined) out.translations = fromLocaleRecord(group.translations);
  return out;
}

function fromSparkJsCheckoutElement(field: Record<string, unknown>): Record<string, unknown> {
  const attributes: Record<string, unknown> = isPlainObject(field.attributes)
    ? { ...field.attributes }
    : {};

  if (isStandardCheckoutElementId(field.id)) {
    const element: Record<string, unknown> = { kind: 'standard', id: field.id };
    for (const key of CHECKOUT_BOOLEAN_ATTRIBUTES) {
      const raw = attributes[key];
      if (typeof raw === 'boolean') element[key] = raw;
    }
    return element;
  }

  const element: Record<string, unknown> = { kind: 'custom', attributes };
  if (typeof field.name === 'string') element.name = field.name;
  if (field.translations !== undefined) element.translations = fromLocaleRecord(field.translations);
  if (Array.isArray(field.options)) element.options = field.options.map(fromSparkJsDropdownGroup);
  // Plain title/detail (no translations set up) pass through to be filed under the fallback locale.
  if (typeof field.title === 'string') element.title = field.title;
  if (typeof field.detail === 'string') element.detail = field.detail;
  return element;
}

/** Tag SparkJS's `checkoutCustomElements` as standard or custom; validation happens later. */
export function fromSparkJsCheckoutElements(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isPlainObject).map(fromSparkJsCheckoutElement);
}

/** Reshape raw spark-options into the dashboard's editor shape (still unvalidated). */
export function fromSparkJsOptions(sparkOptions: Record<string, unknown>): Record<string, unknown> {
  const out = { ...sparkOptions };

  if ('checkoutCustomElements' in out) {
    out.checkoutCustomElements = fromSparkJsCheckoutElements(out.checkoutCustomElements);
  }
  if ('translations' in out) {
    out.translations = fromLocaleRecord(out.translations);
  }

  return out;
}
